import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "sign-in-method",
    title: "วิธีเข้าสู่ระบบ",
    summary:
      "หน้านี้กำหนดว่าผู้ใช้เข้าสู่ระบบได้ด้วยวิธีใดบ้าง (อีเมล/รหัสผ่าน, Google, Microsoft, เบอร์โทรศัพท์) รวมถึงการสมัครสมาชิกเอง ความยาวรหัสผ่านขั้นต่ำ การยืนยันตัวตนหลายขั้นตอน (MFA) และอายุเซสชัน การตั้งค่าในหน้านี้มีผลกับทุกแอปที่ใช้ระบบเข้าสู่ระบบกลางนี้",
    access:
      'เปิดดูได้เมื่อมีสิทธิ์ settings:read (เช่น บทบาท owner, admin, auditor) · แก้ไขได้เมื่อมีสิทธิ์ settings:write (owner, admin) ผู้ที่ดูได้อย่างเดียวจะเห็นช่องต่าง ๆ เป็นสีจาง ไม่มีปุ่ม "แก้ไข" และไม่มีปุ่ม "บันทึกการเปลี่ยนแปลง"',
    shot: {
      file: "sign-in-method-page.png",
      caption: "หน้าวิธีเข้าสู่ระบบ: ตารางผู้ให้บริการด้านบน และส่วน MFA เบอร์โทรศัพท์ และเซสชันด้านล่าง",
    },
    sections: [
      {
        id: "layout",
        heading: "ภาพรวมของหน้า",
        body: [
          'หน้านี้แบ่งเป็น 2 กล่อง กล่องบนคือตารางผู้ให้บริการ (วิธีเข้าสู่ระบบหลัก) แต่ละแถวมีปุ่ม "แก้ไข" ที่เปิดหน้าต่างตั้งค่าของวิธีนั้น และ บันทึกทันที เมื่อกด "บันทึก" ในหน้าต่าง',
          'กล่องล่างมี 3 ส่วน คือ "การยืนยันตัวตนหลายขั้นตอน" "เบอร์โทรศัพท์" และ "เซสชัน" การแก้ไขในกล่องนี้ ยังไม่ถูกบันทึก จนกว่าจะกดปุ่ม "บันทึกการเปลี่ยนแปลง" ที่มุมขวาล่างของกล่อง',
          'เมื่อบันทึกสำเร็จจะมีข้อความ "บันทึกแล้ว" แสดงขึ้น และค่าใหม่มีผลกับการเข้าสู่ระบบครั้งถัดไปของผู้ใช้ทันที ทุกการบันทึกถูกเก็บไว้ในบันทึกการใช้งาน (audit log)',
        ],
        tips: [
          'การกด "บันทึก" ในหน้าต่างของผู้ให้บริการจะบันทึกค่าในกล่องล่างที่แก้ค้างไว้ไปพร้อมกันด้วย ถ้าไม่ต้องการ ให้โหลดหน้าใหม่ก่อนเพื่อทิ้งการแก้ไขที่ยังไม่บันทึก',
        ],
        warnings: [
          'ถ้ามีผู้ดูแลคนอื่นบันทึกการตั้งค่านี้ไปก่อนคุณ ระบบจะไม่เขียนทับ แต่จะแสดงข้อความ "การตั้งค่าถูกแก้ไขโดยผู้อื่นแล้ว — โหลดใหม่เพื่อดูเวอร์ชันล่าสุด" พร้อมปุ่ม "โหลดใหม่" ให้กดโหลดใหม่ ตรวจค่าล่าสุด แล้วแก้ไขซ้ำอีกครั้ง',
        ],
      },
      {
        id: "providers",
        heading: "ตารางผู้ให้บริการ",
        fieldsTitle: "คอลัมน์ในตาราง",
        fields: [
          {
            name: "ผู้ให้บริการ",
            description:
              'ชื่อวิธีเข้าสู่ระบบ: อีเมล/รหัสผ่าน, Google, Microsoft และ Passkey ถ้า Google หรือ Microsoft ยังไม่ได้ตั้งค่าบนเซิร์ฟเวอร์ จะมีข้อความสีส้มใต้ชื่อว่า "ยังไม่ได้ตั้งค่า secret ของ … บนเซิร์ฟเวอร์ — ตั้งค่าใน environment ก่อนเปิดใช้งาน"',
          },
          {
            name: "สถานะ",
            description:
              '"เปิดใช้งาน" (จุดสีเขียว) หรือ "ปิดใช้งาน" (จุดสีเทา) · Passkey แสดง "ยังไม่รองรับ" เพราะระบบยังไม่มีฟีเจอร์นี้ จึงแก้ไขไม่ได้',
          },
          {
            name: 'ปุ่ม "แก้ไข"',
            description: 'เปิดหน้าต่าง "แก้ไข <ชื่อผู้ให้บริการ>" (เห็นเฉพาะผู้มีสิทธิ์ settings:write)',
          },
        ],
      },
      {
        id: "email-password",
        heading: "อีเมล/รหัสผ่าน",
        body: [
          'เป็นวิธีเข้าสู่ระบบหลักของระบบ ผู้ใช้พิมพ์อีเมล (หรือเบอร์โทรศัพท์/รหัสผู้ใช้งาน/ชื่อผู้ใช้ที่ยืนยันแล้ว) และรหัสผ่าน กด "แก้ไข" ในแถว "อีเมล/รหัสผ่าน" เพื่อเปิดหน้าต่างตั้งค่า',
        ],
        shot: {
          file: "sign-in-method-email-password-dialog.png",
          caption: "หน้าต่างแก้ไขอีเมล/รหัสผ่าน",
        },
        fieldsTitle: "ช่องในหน้าต่าง",
        fields: [
          {
            name: "เปิดใช้งาน",
            description:
              "สวิตช์เปิด/ปิดการเข้าสู่ระบบด้วยรหัสผ่าน ถ้าปิด ผู้ใช้จะเข้าสู่ระบบด้วยรหัสผ่านไม่ได้ (รวมถึงการเข้าด้วยเบอร์โทรศัพท์+รหัสผ่านรหัสผู้ใช้งาน+รหัสผ่าน และชื่อผู้ใช้+รหัสผ่าน)",
          },
          {
            name: "อนุญาตให้สมัครสมาชิกเอง",
            description:
              'สวิตช์หลักของการสมัครสมาชิกด้วยตนเอง ถ้าไม่เลือก จะไม่มีใครสมัครเองได้เลย ถ้าเลือก ผู้เข้าชมยังต้องผ่านกฎของประเภทผู้ใช้ด้วย (สมัครได้เมื่ออีเมลตรงกับ Email domains ของประเภทผู้ใช้ใดประเภทหนึ่ง หรือแอปที่ใช้สมัครเปิดให้ "อนุญาตให้สมัครเป็น" ประเภทที่สมัครเองได้) ดูหัวข้อ "ประเภทผู้ใช้"',
          },
          {
            name: "ต้องยืนยันอีเมล",
            description:
              "ถ้าเลือก ผู้ใช้ที่สมัครใหม่ต้องกดลิงก์ในอีเมลยืนยันก่อนจึงจะเข้าสู่ระบบได้ ถ้าไม่เลือก ผู้สมัครจะเข้าสู่ระบบได้ทันทีหลังสมัคร และบัญชีที่ยังไม่ยืนยันอีเมลก็เข้าสู่ระบบได้",
          },
          {
            name: "โดเมนอีเมลที่อนุญาต",
            description:
              'รายชื่อโดเมนที่อนุญาตให้ใช้สมัครสมาชิก เช่น company.co.th พิมพ์โดเมนแล้วกด "เพิ่มโดเมน" หรือกด Enter (ระบบตัด @ ข้างหน้าและแปลงเป็นตัวพิมพ์เล็กให้เอง) โดเมนที่เพิ่มแล้วแสดงเป็นป้าย กดกากบาทบนป้ายเพื่อนำออก · เว้นว่างไว้ = อนุญาตทุกโดเมน · มีผลกับการสมัครเท่านั้น ไม่กระทบบัญชีที่มีอยู่แล้ว · สูงสุด 50 โดเมน',
          },
          {
            name: "ความยาวรหัสผ่านขั้นต่ำ",
            description:
              "จำนวนตัวอักษรขั้นต่ำของรหัสผ่าน ใส่ได้ 8–128 (ค่าเริ่มต้น 12) มีผลเมื่อมีการตั้งหรือเปลี่ยนรหัสผ่านใหม่ (สมัคร, เปลี่ยนรหัสผ่าน, รีเซ็ตรหัสผ่าน, ผู้ดูแลตั้งรหัสผ่านให้) รหัสผ่านเดิมของผู้ใช้ยังใช้ได้ตามปกติ",
          },
        ],
        steps: [
          'กด "แก้ไข" ในแถว "อีเมล/รหัสผ่าน"',
          'ปรับค่าที่ต้องการ เช่น ติ๊ก "อนุญาตให้สมัครสมาชิกเอง" หรือเพิ่มโดเมนบริษัทใน "โดเมนอีเมลที่อนุญาต"',
          'กด "บันทึก" — ระบบบันทึกทันทีและปิดหน้าต่าง (กด "ยกเลิก" เพื่อทิ้งการแก้ไขในหน้าต่างนี้)',
        ],
        warnings: [
          'การปิดสวิตช์ "เปิดใช้งาน" ของอีเมล/รหัสผ่าน ทำให้ผู้ใช้ส่วนใหญ่ (รวมถึงผู้ดูแลระบบเอง) เข้าสู่ระบบด้วยรหัสผ่านไม่ได้ ตรวจให้แน่ใจว่ามีวิธีเข้าสู่ระบบอื่นที่ใช้งานได้ก่อนปิด',
          'การปิด "ต้องยืนยันอีเมล" ทำให้ใครก็ได้สมัครด้วยอีเมลที่ไม่ใช่ของตนเองแล้วเข้าใช้ได้ทันที ควรเปิดไว้เสมอในระบบจริง',
        ],
      },
      {
        id: "social",
        heading: "Google และ Microsoft",
        body: [
          'ให้ผู้ใช้กดปุ่ม "เข้าสู่ระบบด้วย Google/Microsoft" ในหน้าเข้าสู่ระบบ หน้าต่างแก้ไขมีสวิตช์ "เปิดใช้งาน" เพียงอย่างเดียว',
          "ก่อนเปิดใช้งาน ทีมผู้ดูแลเซิร์ฟเวอร์ต้องตั้งค่า client ID / secret ของ Google หรือ Microsoft บนเซิร์ฟเวอร์ก่อน ถ้ายังไม่ได้ตั้ง หน้าต่างจะแสดงคำเตือนสีส้ม และการเปิดสวิตช์จะยังใช้งานจริงไม่ได้",
          "เมื่อผู้ใช้ใหม่เข้าสู่ระบบด้วย Google/Microsoft เป็นครั้งแรก ระบบจะสร้างบัญชีให้ โดยเลือกประเภทผู้ใช้จากโดเมนอีเมลก่อน ถ้าไม่ตรง จะใช้ประเภทของแอปแรกที่เปิดให้สมัคร ถ้าไม่มีเลย การเข้าสู่ระบบครั้งแรกจะถูกปฏิเสธ",
        ],
        shot: {
          file: "sign-in-method-google-dialog.png",
          caption: "หน้าต่างแก้ไข Google (มีคำเตือนเมื่อยังไม่ได้ตั้งค่า secret บนเซิร์ฟเวอร์)",
        },
      },
      {
        id: "passkey",
        heading: "Passkey",
        body: ['แสดงสถานะ "ยังไม่รองรับ" เพราะระบบยังไม่มีการเข้าสู่ระบบด้วย Passkey จึงไม่มีปุ่มแก้ไข'],
      },
      {
        id: "mfa",
        heading: "การยืนยันตัวตนหลายขั้นตอน",
        body: [
          'MFA (หรือ 2FA) คือการขอรหัสอีกชั้นหลังใส่รหัสผ่าน เพื่อป้องกันกรณีรหัสผ่านรั่ว ส่วนนี้อยู่ในกล่องล่าง ต้องกด "บันทึกการเปลี่ยนแปลง" หลังแก้ไข',
        ],
        shot: {
          file: "sign-in-method-settings.png",
          caption: "กล่องล่าง: การยืนยันตัวตนหลายขั้นตอน เบอร์โทรศัพท์ และเซสชัน",
        },
        fieldsTitle: "ช่องในส่วนนี้",
        fields: [
          {
            name: "นโยบาย",
            description:
              '"ไม่บังคับ" = ผู้ใช้เลือกเปิด MFA เองได้ · "บังคับสำหรับผู้ดูแลระบบ" (ค่าเริ่มต้น) = ผู้ที่มีบทบาทที่แก้ไขข้อมูลได้ (เช่น owner, admin หรือบทบาทที่สร้างเองที่มีสิทธิ์ประเภทเขียน/ลบ/นำเข้า) ต้องตั้ง MFA ก่อนเข้าแอปใด ๆ · "บังคับทุกคน" = ทุกบัญชีต้องตั้ง MFA',
          },
          {
            name: "วิธีการ",
            description:
              'วิธี MFA ที่ผู้ใช้เลือกได้: "แอปยืนยันตัวตน (TOTP)" เช่น Google Authenticator, Microsoft Authenticator และ "รหัสทางอีเมล" ต้องเลือกอย่างน้อย 1 วิธี ไม่เช่นนั้นจะบันทึกไม่ผ่าน',
          },
        ],
        tips: [
          'ประเภทผู้ใช้แต่ละประเภทกำหนด "กฎ 2FA" ของตัวเองได้ (หน้า การตั้งค่า → ประเภทผู้ใช้) ซึ่งจะ แทนที่ นโยบายในหน้านี้ เช่น ตั้ง customer เป็น "ไม่บังคับ" ลูกค้าจะไม่ถูกบังคับแม้นโยบายรวมจะเป็น "บังคับทุกคน"',
          "ผู้ใช้ที่ถูกบังคับแต่ยังไม่ได้ตั้ง MFA จะถูกพาไปหน้าตั้งค่า MFA ทันทีหลังเข้าสู่ระบบ และจะใช้แอปใดไม่ได้จนกว่าจะตั้งเสร็จ",
        ],
        warnings: [
          'การเปลี่ยนเป็น "บังคับทุกคน" ทำให้ผู้ใช้ทุกคนที่ยังไม่มี MFA ต้องตั้งค่าในการเข้าสู่ระบบครั้งถัดไป ควรแจ้งผู้ใช้ล่วงหน้า',
          'ถ้าเอา "รหัสทางอีเมล" ออก ผู้ใช้ที่ใช้วิธีนี้อยู่อาจต้องตั้ง MFA ใหม่ด้วยแอปยืนยันตัวตน',
        ],
      },
      {
        id: "phone",
        heading: "เบอร์โทรศัพท์",
        fieldsTitle: "ช่องในส่วนนี้",
        fields: [
          {
            name: "อนุญาตให้เข้าสู่ระบบด้วยเบอร์โทรศัพท์ที่ยืนยันแล้วและรหัสผ่าน",
            description:
              "ผู้ใช้พิมพ์เบอร์โทรศัพท์ที่ยืนยันแล้วในช่องเข้าสู่ระบบแทนอีเมลได้ (เช่น 081-234-5678 หรือ +66812345678) แล้วใส่รหัสผ่านตามปกติ",
          },
          {
            name: "อนุญาตให้เข้าสู่ระบบด้วยรหัสครั้งเดียวทาง SMS",
            description:
              "ผู้ใช้ขอรหัส OTP ทาง SMS ไปที่เบอร์ที่ยืนยันแล้ว แล้วใช้รหัสนั้นเข้าสู่ระบบโดยไม่ต้องใช้รหัสผ่าน (ถ้าผู้ใช้เปิด MFA ไว้ ยังต้องผ่าน MFA อีกชั้น) เลือกได้เฉพาะเมื่อเปิดตัวเลือกแรกแล้ว",
          },
        ],
        body: [
          'ถ้าเซิร์ฟเวอร์ยังไม่ได้ตั้งค่าผู้ให้บริการ SMS จะมีกล่องคำเตือนสีส้ม "เซิร์ฟเวอร์ยังไม่ได้ตั้งค่า SMS (SMS_PROVIDER) — ผู้ใช้ยืนยันเบอร์เองไม่ได้ และยังใช้รหัสทาง SMS ไม่ได้" ในกรณีนี้ผู้ดูแลยังเพิ่มเบอร์โทรศัพท์ให้ผู้ใช้แบบ "ยืนยันแล้ว" ได้ (ในหน้ารายละเอียดผู้ใช้ แท็บ อีเมล/เบอร์โทร) และผู้ใช้ใช้เบอร์+รหัสผ่านเข้าสู่ระบบได้ แต่ส่ง SMS ไม่ได้',
          "รหัสผู้ใช้งาน (เช่น รหัสพนักงาน 100234) และชื่อผู้ใช้ใช้เข้าสู่ระบบได้เสมอ แม้จะปิดการเข้าด้วยเบอร์โทรศัพท์",
        ],
      },
      {
        id: "session",
        heading: "เซสชัน",
        fieldsTitle: "ช่องในส่วนนี้",
        fields: [
          {
            name: "อายุเซสชัน (วัน)",
            description:
              "ผู้ใช้จะอยู่ในสถานะเข้าสู่ระบบได้นานกี่วันก่อนต้องเข้าสู่ระบบใหม่ ใส่ได้ 1–365 (ค่าเริ่มต้น 7) มีผลกับเซสชันที่สร้างใหม่หลังบันทึก",
            required: true,
          },
          {
            name: "หมดเวลาเมื่อไม่ใช้งาน (นาที, ไม่บังคับ)",
            description:
              'ใส่ได้ตั้งแต่ 5 นาทีขึ้นไป หรือเว้นว่างเพื่อไม่ใช้ · หมายเหตุ: เวอร์ชันปัจจุบันบันทึกค่านี้ไว้แต่ยังไม่ได้ตัดเซสชันตามเวลาที่ไม่ใช้งานอย่างเคร่งครัด เซสชันยังหมดอายุตาม "อายุเซสชัน (วัน)" เป็นหลัก',
          },
        ],
        steps: [
          'แก้ค่าที่ต้องการในส่วน "การยืนยันตัวตนหลายขั้นตอน" "เบอร์โทรศัพท์" หรือ "เซสชัน"',
          'กด "บันทึกการเปลี่ยนแปลง" ที่มุมขวาล่าง',
          'รอข้อความ "บันทึกแล้ว" ถ้ามีข้อความผิดพลาดสีแดงด้านบน ให้แก้ตามข้อความแล้วบันทึกอีกครั้ง',
        ],
      },
      {
        id: "common-tasks",
        heading: "งานที่ทำบ่อย",
        steps: [
          'เปิดให้ลูกค้าสมัครเอง: เปิด "อนุญาตให้สมัครสมาชิกเอง" ในอีเมล/รหัสผ่าน แล้วไปที่แอปของลูกค้า (OAuth clients → แอป → แท็บสิทธิ์เข้าถึง) ตั้ง "อนุญาตให้สมัครเป็น" เป็น customer',
          'ให้พนักงานสมัครเองด้วยอีเมลบริษัท: เปิด "อนุญาตให้สมัครสมาชิกเอง" แล้วไปที่ การตั้งค่า → ประเภทผู้ใช้ → แก้ไข employee → ใส่โดเมนบริษัทใน Email domains',
          'บังคับ MFA ให้ทุกคน: เลือกนโยบาย "บังคับทุกคน" แล้วกด "บันทึกการเปลี่ยนแปลง"',
          'ปิดการขอรหัสทาง SMS ชั่วคราว: เอาเครื่องหมายออกจาก "อนุญาตให้เข้าสู่ระบบด้วยรหัสครั้งเดียวทาง SMS" แล้วบันทึก',
        ],
      },
    ],
    related: ["user-types", "client-detail", "users", "audit-logs"],
  },
  en: {
    id: "sign-in-method",
    title: "Sign-in method",
    summary:
      "This page decides how people sign in (email/password, Google, Microsoft, phone numbers), whether they may sign up themselves, the minimum password length, multi-factor authentication (MFA) and how long sessions last. The settings apply to every application that uses this sign-in service.",
    access:
      'Viewing needs the settings:read permission (e.g. the owner, admin and auditor roles). Changing needs settings:write (owner, admin). Read-only viewers see the fields greyed out, with no "Edit" buttons and no "Save changes" button.',
    shot: {
      file: "sign-in-method-page.png",
      caption: "The Sign-in method page: providers table on top; MFA, phone numbers and session below",
    },
    sections: [
      {
        id: "layout",
        heading: "How the page is organised",
        body: [
          'The page has two boxes. The top box is the providers table (the main sign-in methods); each row has an "Edit" button that opens that method\'s settings window, which saves immediately when you click "Save" in the window.',
          'The bottom box has three sections: "Multi-factor authentication", "Phone numbers" and "Session". Changes there are not saved until you click "Save changes" at the bottom right of the box.',
          'After a successful save a "Saved" message appears and the new values apply to users\' next sign-in right away. Every save is recorded in the audit log.',
        ],
        tips: [
          'Clicking "Save" in a provider window also saves any unsaved edits in the bottom box. If you do not want that, reload the page first to discard them.',
        ],
        warnings: [
          'If another admin saved these settings after you opened the page, your save does not overwrite theirs. You see "Settings were changed by someone else — reload to see the latest version." with a "Reload" button. Reload, check the latest values and make your change again.',
        ],
      },
      {
        id: "providers",
        heading: "Providers table",
        fieldsTitle: "Columns in the table",
        fields: [
          {
            name: "Provider",
            description:
              'The sign-in method: Email/Password, Google, Microsoft and Passkey. If Google or Microsoft is not set up on the server, an orange note under the name says "Server secrets for … are not configured — set them in the server environment before enabling."',
          },
          {
            name: "Status",
            description:
              '"Enabled" (green dot) or "Disabled" (grey dot). Passkey shows "Not supported yet" because the feature does not exist yet, so it cannot be edited.',
          },
          {
            name: '"Edit" button',
            description: 'Opens the "Edit <provider>" window (only for people with settings:write).',
          },
        ],
      },
      {
        id: "email-password",
        heading: "Email/Password",
        body: [
          'The main sign-in method: people type their email (or a verified phone number, user code or username) and their password. Click "Edit" in the "Email/Password" row to open its settings.',
        ],
        shot: {
          file: "sign-in-method-email-password-dialog.png",
          caption: "The Edit Email/Password window",
        },
        fieldsTitle: "Fields in the window",
        fields: [
          {
            name: "Enable",
            description:
              "Turns password sign-in on or off. When off, nobody can sign in with a password (this includes phone number + password, user code + password and username + password).",
          },
          {
            name: "Allow users to sign up",
            description:
              'The master switch for self sign-up. When unticked, nobody can sign up. When ticked, visitors must still pass the user-type rules: sign-up works when the email matches the Email domains of a user type, or when the application they sign up through offers "Allow sign-up as" a type that allows self sign-up. See "User types".',
          },
          {
            name: "Require email verification",
            description:
              "When ticked, new accounts must click the link in the verification email before they can sign in. When unticked, people can sign in right after signing up, and accounts with an unverified email can sign in.",
          },
          {
            name: "Allowed email domains",
            description:
              'Domains that may be used to sign up, e.g. company.co.th. Type a domain and click "Add domain" or press Enter (a leading @ is removed and the text is lower-cased). Added domains show as chips; click the cross on a chip to remove it. Empty = any domain. Applies to sign-up only, not to existing accounts. Up to 50 domains.',
          },
          {
            name: "Minimum password length",
            description:
              "Minimum number of characters, 8–128 (default 12). Applies whenever a password is set or changed (sign-up, change password, reset, admin sets a password). Existing passwords keep working.",
          },
        ],
        steps: [
          'Click "Edit" in the "Email/Password" row.',
          'Change what you need, e.g. tick "Allow users to sign up" or add your company domain under "Allowed email domains".',
          'Click "Save" — the change is saved at once and the window closes ("Cancel" discards the changes made in the window).',
        ],
        warnings: [
          'Turning "Enable" off for Email/Password stops most people — including admins — from signing in with a password. Make sure another working sign-in method exists first.',
          'Turning "Require email verification" off lets anyone sign up with an address that is not theirs and use it immediately. Keep it on in production.',
        ],
      },
      {
        id: "social",
        heading: "Google and Microsoft",
        body: [
          'Adds a "Sign in with Google/Microsoft" button to the sign-in page. The edit window has a single "Enable" switch.',
          "Before enabling, the server team must configure the Google or Microsoft client ID / secret on the server. If that is missing, the window shows an orange warning and the switch has no real effect.",
          "When a new person signs in with Google/Microsoft for the first time, an account is created. Its user type comes from the email domain first; otherwise from the first application that offers sign-up; if neither applies, the first sign-in is refused.",
        ],
        shot: {
          file: "sign-in-method-google-dialog.png",
          caption: "The Edit Google window (with a warning when the server secrets are missing)",
        },
      },
      {
        id: "passkey",
        heading: "Passkey",
        body: ['Shows "Not supported yet": passkey sign-in is not available, so there is no Edit button.'],
      },
      {
        id: "mfa",
        heading: "Multi-factor authentication",
        body: [
          'MFA (also called 2FA) asks for a second code after the password, protecting accounts whose password leaked. This section is in the bottom box — click "Save changes" after editing.',
        ],
        shot: {
          file: "sign-in-method-settings.png",
          caption: "The bottom box: Multi-factor authentication, Phone numbers and Session",
        },
        fieldsTitle: "Fields in this section",
        fields: [
          {
            name: "Policy",
            description:
              '"Optional" = people turn MFA on if they want. "Required for administrators" (default) = anyone whose roles can change things (e.g. owner, admin, or a custom role with a write, delete or import permission) must set up MFA before using any application. "Required for everyone" = every account must set up MFA.',
          },
          {
            name: "Methods",
            description:
              'Which MFA methods people can choose: "Authenticator app (TOTP)" such as Google Authenticator or Microsoft Authenticator, and "Code by email". At least one must be ticked, otherwise saving fails.',
          },
        ],
        tips: [
          'Each user type can have its own "Two-factor rule" (Settings → User types) that overrides this policy. For example, with customer set to "Optional", customers are never forced even when the policy here is "Required for everyone".',
          "Someone who is required to use MFA but has not set it up is taken to the MFA setup page right after signing in and cannot use any application until it is done.",
        ],
        warnings: [
          'Switching to "Required for everyone" makes every user without MFA set it up at their next sign-in. Tell users beforehand.',
          'Removing "Code by email" may force people who use it to set up MFA again with an authenticator app.',
        ],
      },
      {
        id: "phone",
        heading: "Phone numbers",
        fieldsTitle: "Fields in this section",
        fields: [
          {
            name: "Allow signing in with a verified phone number and password",
            description:
              "People can type a verified phone number instead of an email (e.g. 081-234-5678 or +66812345678) and then their password.",
          },
          {
            name: "Allow signing in with a one-time code by SMS",
            description:
              "People request a one-time code by SMS to their verified number and sign in with it, without a password (if they have MFA, the second step still applies). Only available when the first option is on.",
          },
        ],
        body: [
          'If no SMS provider is configured on the server, an orange box says "SMS is not configured on the server (SMS_PROVIDER) — phone numbers cannot be verified by the user and SMS codes are unavailable." Admins can still add phone numbers as verified (user page → Emails & phones tab) and phone + password sign-in works, but no SMS can be sent.',
          "User codes (e.g. employee code 100234) and usernames always work for signing in, even when phone sign-in is off.",
        ],
      },
      {
        id: "session",
        heading: "Session",
        fieldsTitle: "Fields in this section",
        fields: [
          {
            name: "Session lifetime (days)",
            description:
              "How many days people stay signed in before they must sign in again, 1–365 (default 7). Applies to sessions created after saving.",
            required: true,
          },
          {
            name: "Idle timeout (minutes, optional)",
            description:
              '5 minutes or more, or leave empty for none. Note: the current version stores this value but does not yet strictly end sessions after inactivity; sessions mainly expire by "Session lifetime (days)".',
          },
        ],
        steps: [
          'Change values in "Multi-factor authentication", "Phone numbers" or "Session".',
          'Click "Save changes" at the bottom right.',
          'Wait for "Saved". If a red error appears at the top, correct the value and save again.',
        ],
      },
      {
        id: "common-tasks",
        heading: "Common tasks",
        steps: [
          'Let customers sign up: turn on "Allow users to sign up" under Email/Password, then open the customer application (OAuth clients → application → Access tab) and set "Allow sign-up as" to customer.',
          'Let employees sign up with the company email: turn on "Allow users to sign up", then go to Settings → User types → edit employee → add the company domain under Email domains.',
          'Require MFA for everyone: choose the policy "Required for everyone" and click "Save changes".',
          'Temporarily stop SMS codes: untick "Allow signing in with a one-time code by SMS" and save.',
        ],
      },
    ],
    related: ["user-types", "client-detail", "users", "audit-logs"],
  },
};

export default topic;
